/**
 * MONSTER TOWN — PROP ANIMATION RUNTIME. OWNED BY THE INTERIORS CREW.
 *
 * LAW #1 SAYS EVERY TAP DOES SOMETHING. `src/app/interactions.ts` turns a
 * placeable's `InteractionResult` into `prop:anim` / `prop:toggle` events — and
 * until this file existed those events went nowhere. This is the half that a
 * kid actually SEES: the lid lifts, the door swings, the bed bounces, the bulb
 * lights up.
 *
 * HOW AN ART BUILDER OPTS IN
 *   - Name the part you want moved:            `mesh.name = 'lid'`
 *   - Return `{ kind:'anim', clip:'lid', target:'lid' }` from `interact`.
 *   - For on/off art, build BOTH states and name them `bulb` and `bulbOff`;
 *     `{ kind:'toggle', on, target:'bulb' }` swaps them. Read
 *     `ctx.instance?.state` in `buildArt` so the state survives a reload.
 *
 * Everything is transform-only (position / rotation / scale) on top of the
 * part's authored transform, which is captured on the first frame and restored
 * when the clip ends. No new geometry, no new materials, no allocation per
 * frame — an older iPad runs this for free.
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { world } from '../core/scene'
import type { InstanceId } from '../core/types'

/** A clip writes deltas into `out` for u in 0..1. Deterministic, no rng. */
type Clip = (u: number, out: Delta) => void

interface Delta {
  px: number
  py: number
  pz: number
  rx: number
  ry: number
  rz: number
  sx: number
  sy: number
  sz: number
}

const SIN = Math.sin
const PI = Math.PI

function reset(d: Delta): Delta {
  d.px = 0
  d.py = 0
  d.pz = 0
  d.rx = 0
  d.ry = 0
  d.rz = 0
  d.sx = 1
  d.sy = 1
  d.sz = 1
  return d
}

/**
 * THE CLIP LIBRARY. Adding a new prop motion is a line here, never an engine
 * change — same rule as content. Keep every clip returning to zero at u=1 so a
 * prop can never be left in a broken pose.
 */
const CLIPS: Record<string, Clip> = {
  /** Knocked. The default for anything solid. */
  wobble: (u, d) => {
    const k = (1 - u) * SIN(u * PI * 5)
    d.rz = k * 0.17
    d.px = k * 0.03
  },
  /** Happy hop. */
  bounce: (u, d) => {
    const k = Math.abs(SIN(u * PI * 2)) * (1 - u * 0.4)
    d.py = k * 0.34
    d.sy = 1 + k * 0.12
    d.sx = 1 - k * 0.07
    d.sz = 1 - k * 0.07
  },
  /** Gentle breathing float — flowers, plants, steam. */
  bob: (u, d) => {
    d.py = SIN(u * PI * 2) * 0.11
    d.rz = SIN(u * PI * 2) * 0.05
  },
  /** One full turn. */
  spin: (u, d) => {
    d.ry = u * PI * 2
  },
  /** A door / fridge / wardrobe swinging open and easing shut. */
  swing: (u, d) => {
    d.ry = -SIN(u * PI) * 1.25
  },
  /** A lid hinging back and settling. */
  lid: (u, d) => {
    d.rx = -SIN(u * PI) * 1.1
    d.py = SIN(u * PI) * 0.05
  },
  /** Squish — cushions, mattresses, anything soft. */
  squash: (u, d) => {
    const k = SIN(u * PI)
    d.sy = 1 - k * 0.26
    d.sx = 1 + k * 0.15
    d.sz = 1 + k * 0.15
    d.py = -k * 0.04
  },
  /** Scale pop — something appearing or being noticed. */
  pop: (u, d) => {
    const k = SIN(u * PI)
    d.sx = 1 + k * 0.32
    d.sy = 1 + k * 0.32
    d.sz = 1 + k * 0.32
  },
  /** Fast little shiver. */
  jiggle: (u, d) => {
    const k = (1 - u) * SIN(u * PI * 9)
    d.px = k * 0.05
    d.pz = k * 0.04
  },
  /** Angry rattle — appliances, alarm clocks. */
  shake: (u, d) => {
    const k = (1 - u) * SIN(u * PI * 11)
    d.rz = k * 0.09
    d.px = k * 0.035
  },
  /** Lifts up and settles back — a plate served, a plant growing. */
  rise: (u, d) => {
    const k = SIN(u * PI)
    d.py = k * 0.5
    d.sy = 1 + k * 0.08
  },
  /** A drawer / tray sliding out and back. */
  slide: (u, d) => {
    d.pz = SIN(u * PI) * 0.34
  },
  /** Whole tumble — TV channel change, toy flipping. */
  flip: (u, d) => {
    d.rx = u * PI * 2
  },
  /** Clock hands / pendulum. */
  tick: (u, d) => {
    d.rz = (1 - u) * SIN(u * PI * 6) * 0.3
  },
  /** Water / fish / bubbles drifting. */
  swim: (u, d) => {
    d.px = SIN(u * PI * 2) * 0.16
    d.ry = SIN(u * PI * 2) * 0.4
    d.py = SIN(u * PI * 4) * 0.05
  },
  /** Sleepy sag and rise — the bed, a napping monster. */
  breathe: (u, d) => {
    const k = SIN(u * PI * 2)
    d.sy = 1 + k * 0.06
    d.py = k * 0.03
  },
}

export function clipNames(): string[] {
  return Object.keys(CLIPS)
}

interface Active {
  obj: THREE.Object3D
  clip: Clip
  t: number
  dur: number
  basePos: THREE.Vector3
  baseRot: THREE.Euler
  baseScale: THREE.Vector3
}

const active = new Map<string, Active>()
const scratch: Delta = reset({
  px: 0,
  py: 0,
  pz: 0,
  rx: 0,
  ry: 0,
  rz: 0,
  sx: 1,
  sy: 1,
  sz: 1,
})

/** Find an instance's art in whichever scene holds it. */
function artOf(instanceId: InstanceId): THREE.Object3D | null {
  for (const id of world.ids()) {
    const node = world.get(id)
    const art = node?.art.get(instanceId)
    if (art !== undefined) return art
  }
  return null
}

/**
 * Resolve the sub-object to move. Falls back to the whole instance group so a
 * lazy `{ kind:'anim', clip:'wobble' }` with no target still reads as a
 * reaction rather than nothing — LAW #1 has no "almost".
 */
function partOf(root: THREE.Object3D, target: string | undefined): THREE.Object3D {
  if (target === undefined || target.length === 0) return root
  return root.getObjectByName(target) ?? root
}

function begin(obj: THREE.Object3D, clipName: string, seconds: number): void {
  const clip = CLIPS[clipName] ?? CLIPS['wobble']
  if (clip === undefined) return
  const existing = active.get(obj.uuid)
  if (existing !== undefined) {
    // Re-tapped mid-clip: restart from the SAME base pose, never from the
    // animated one, or repeated taps would walk a prop across the room.
    existing.clip = clip
    existing.t = 0
    existing.dur = Math.max(0.08, seconds)
    return
  }
  active.set(obj.uuid, {
    obj,
    clip,
    t: 0,
    dur: Math.max(0.08, seconds),
    basePos: obj.position.clone(),
    baseRot: obj.rotation.clone(),
    baseScale: obj.scale.clone(),
  })
}

function restore(a: Active): void {
  a.obj.position.copy(a.basePos)
  a.obj.rotation.copy(a.baseRot)
  a.obj.scale.copy(a.baseScale)
}

/**
 * Visual on/off. Convention: a part named `x` is the ON art and an optional
 * sibling named `xOff` is the OFF art. Either may be missing.
 */
function applyToggle(root: THREE.Object3D, target: string | undefined, on: boolean): void {
  const name = target ?? 'on'
  const onPart = root.getObjectByName(name)
  const offPart = root.getObjectByName(`${name}Off`)
  if (onPart !== undefined) onPart.visible = on
  if (offPart !== undefined) offPart.visible = !on
  // Even when a builder named nothing, the whole prop gives a little pop so the
  // kid sees that their tap landed.
  begin(onPart ?? root, 'pop', 0.32)
}

export function installPropAnimation(): void {
  bus.on('prop:anim', (e) => {
    const root = artOf(e.instanceId)
    if (root === null) return
    begin(partOf(root, e.target), e.clip, e.seconds)
  })

  bus.on('prop:toggle', (e) => {
    const root = artOf(e.instanceId)
    if (root === null) return
    applyToggle(root, e.target, e.on)
  })

  // A rebuilt or removed instance leaves orphan art behind; drop anything whose
  // object is no longer in the graph so we never animate a detached mesh.
  bus.on('build:removed', () => sweep())
  bus.on('build:artStale', () => sweep())
  bus.on('scene:left', () => stopAll())

  engine.addSystem({
    id: 'interactions.propAnim',
    order: 62,
    fixed: (dt) => step(dt),
  })
}

function sweep(): void {
  for (const [key, a] of active) {
    if (a.obj.parent === null) {
      active.delete(key)
    }
  }
}

/** Restore every animated part and clear. Used on scene change. */
export function stopAll(): void {
  for (const a of active.values()) restore(a)
  active.clear()
}

function step(dt: number): void {
  if (active.size === 0) return
  for (const [key, a] of active) {
    if (a.obj.parent === null) {
      active.delete(key)
      continue
    }
    a.t += dt
    const u = Math.min(1, a.t / a.dur)
    reset(scratch)
    a.clip(u, scratch)
    a.obj.position.set(
      a.basePos.x + scratch.px,
      a.basePos.y + scratch.py,
      a.basePos.z + scratch.pz,
    )
    a.obj.rotation.set(
      a.baseRot.x + scratch.rx,
      a.baseRot.y + scratch.ry,
      a.baseRot.z + scratch.rz,
    )
    a.obj.scale.set(
      a.baseScale.x * scratch.sx,
      a.baseScale.y * scratch.sy,
      a.baseScale.z * scratch.sz,
    )
    if (u >= 1) {
      restore(a)
      active.delete(key)
    }
  }
}

/** Debug/harness: how many parts are moving right now. */
export function activeAnimCount(): number {
  return active.size
}
